// ============================================================================
// QUOTE LETTERHEAD MARKS: which logo, badges and services line a quote prints
// ----------------------------------------------------------------------------
// The quote letterhead (FullQuoteEditorModal, #quote-print-content) carries
// three marks that are RJ Bird Building's and read no profile field:
//   - the MBA and HIA member badges (RJ Bird's memberships);
//   - the services line "BUILDING MAINTENANCE - PROJECT MANAGEMENT - ...";
//   - the bundled logo, assets/Logo_header_large.png, which is RJ Bird's logo.
// Printing them on another business's quote tells that business's clients it
// holds RJ Bird's memberships and trades under RJ Bird's logo. So they print
// only for RJ Bird. Any other business prints its own logo, or, until it sets
// one in Settings, its own name as a plain wordmark.
//
// "RJ Bird" here is RJ Bird's profile, recognised by its stored name
// (isRjBirdTenantProfile), and never a demo business. A business that is not
// known yet (profile not loaded, or read back with no legal or trading name)
// prints none of the three marks and a blank logo placeholder, unless it has
// a logo of its own (D9, 2026-10-06). Before D9 the letterhead used
// isRjBirdTenant, which treats an unknown profile as RJ Bird, so a business
// whose profile was still loading showed RJ Bird's logo, badges and services
// line. isRjBirdTenant itself is unchanged: the quote prefix, rates and its
// other callers still read an unknown profile as RJ Bird. RJ Bird's own
// letterhead, once its profile (or its last good profile) is in hand, is
// unchanged. The wordmark is drawn here, in the browser, rather than
// stored as the business's logo: the global JSON guard refuses any data:image/
// value in a JSON write, so a stored wordmark would make every Settings save
// for that business fail (Settings sends the whole profile row).
// ============================================================================

import { isDemoBusiness } from '../services/demo-business';
import { isRjBirdTenantProfile } from '../services/rj-bird-rate-card-sync';
import { DEFAULT_ACCENT, accentContrastOn, contrastRatio, deriveAccentStops } from '../services/brand-accent';

/** RJ Bird Building's logo, bundled with the app. */
export const RJ_BIRD_LOGO_PATH = 'assets/Logo_header_large.png';

/** RJ Bird Building's signature image, bundled with the app. */
export const RJ_BIRD_SIGNATURE_PATH = 'assets/Sign_RJB.png';

const WORDMARK_MAX_CHARS = 60;

function escapeXml(text: string): string {
    return text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;').replace(/'/g, '&apos;');
}

/**
 * The business's name as a plain wordmark image (an SVG data URI) for the
 * quote's logo slot. Dark type, because the logo bar is a coloured block;
 * white type on a business colour too dark for dark type (quoteAccentFor).
 */
export function wordmarkLogo(name: unknown, ink: '#111111' | '#ffffff' = '#111111'): string {
    const cleaned = String(name ?? '')
        // Control characters are not allowed in XML text; a lone surrogate
        // makes encodeURIComponent throw.
        // eslint-disable-next-line no-control-regex
        .replace(/[\u0000-\u001f\u007f]/g, '')
        .replace(/[\uD800-\uDBFF](?![\uDC00-\uDFFF])|(?<![\uD800-\uDBFF])[\uDC00-\uDFFF]/g, '')
        .replace(/\s+/g, ' ')
        .trim();
    // By code point, so an emoji is never cut in half.
    const label = Array.from(cleaned).slice(0, WORDMARK_MAX_CHARS).join('').trim();
    const width = Math.max(240, Array.from(label).length * 26);
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="80" viewBox="0 0 ${width} 80">`
        + '<text x="50%" y="55" text-anchor="middle" font-family="Arial, Helvetica, sans-serif" '
        + `font-size="44" font-weight="700" fill="${ink}">${escapeXml(label)}</text></svg>`;
    return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
}

/**
 * The logo slot while the business is not known yet: an empty wordmark, so
 * the bar keeps its height and shows nobody's name or logo.
 */
export const NEUTRAL_LETTERHEAD_LOGO = wordmarkLogo('');

export interface LetterheadMarks {
    /** The image in the letterhead's logo slot. */
    logoSrc: string;
    /** The MBA and HIA member badges beside the logo. */
    showMemberBadges: boolean;
    /** RJ Bird's services line under the business details. */
    showServicesTagline: boolean;
}

interface LetterheadProfile {
    legalName?: string | null;
    tradingName?: string | null;
    logoPath?: string | null;
}

/**
 * The quote's logo size (the logo bar in FullQuoteEditorModal): the logo 47px
 * high on a block of the quote's colour (quoteAccentFor(profile).fill)
 * padded 6px 16px with 6px corners. The Stage 2 report, the costings header
 * and the Work Method print a logo the same way (G7).
 */
export const LETTERHEAD_LOGO_HEIGHT_PX = 47;
export const LETTERHEAD_LOGO_BLOCK = Object.freeze({
    borderRadius: '6px',
    padding: '6px 16px',
});

/** Known means a legal or trading name, the same test the quote terms use. */
function isKnownProfile(profile: LetterheadProfile | null | undefined): boolean {
    return Boolean(String(profile?.legalName ?? '').trim() || String(profile?.tradingName ?? '').trim());
}

/**
 * The business's logo, or null when it has none: a logo it set in Settings,
 * else RJ Bird's bundled logo for RJ Bird. A profile that is not known yet
 * keeps a logo it set, but never gets the bundled RJ Bird logo. The quote's
 * logo slot prints this image and, where it is null, a wordmark or a blank
 * placeholder. The other documents already print the name as text, so where
 * it is null they print no logo (G7).
 */
export function businessLogoSrc(profile: LetterheadProfile | null | undefined): string | null {
    const ownLogo = String(profile?.logoPath ?? '').trim();
    if (!isKnownProfile(profile)) return ownLogo && ownLogo !== RJ_BIRD_LOGO_PATH ? ownLogo : null;
    const rjBird = !isDemoBusiness() && isRjBirdTenantProfile(profile);
    return ownLogo || (rjBird ? RJ_BIRD_LOGO_PATH : null);
}

export function resolveLetterheadMarks(profile: LetterheadProfile | null | undefined): LetterheadMarks {
    const rjBird = !isDemoBusiness() && isRjBirdTenantProfile(profile);
    const name = String(profile?.tradingName || profile?.legalName || '').trim();
    const logo = businessLogoSrc(profile);
    if (!isKnownProfile(profile)) {
        // Not known yet: the business's own logo or a blank placeholder.
        return {
            logoSrc: logo || NEUTRAL_LETTERHEAD_LOGO,
            showMemberBadges: false,
            showServicesTagline: false,
        };
    }
    return {
        logoSrc: logo || wordmarkLogo(name, quoteAccentFor(profile).wordmarkInk),
        showMemberBadges: rjBird,
        showServicesTagline: rjBird,
    };
}

/**
 * The signature image in a quote's Authorisation block, or null for none.
 *
 * D7 follow-up (2026-10-06). The block printed `customSignature ||
 * RJ_BIRD_SIGNATURE_PATH`, so every business that had not uploaded a
 * signature signed its quotes with RJ Bird's. Now:
 *   - a signature the business uploaded always prints;
 *   - RJ Bird, recognised by its stored name (not its RBB prefix, which
 *     another business's initials can share), prints its own image;
 *   - any other business, the demo (D9, 2026-10-06: it printed RJ Bird's
 *     image before), and one whose profile is not known yet, print no
 *     signature image. Stage 5 holds print, PDF and email for an unknown
 *     business anyway, so an unknown profile only affects the preview.
 */
export function quoteSignatureSrc(customSignature: unknown, profile: unknown): string | null {
    const own = typeof customSignature === 'string' ? customSignature.trim() : '';
    if (own) return customSignature as string;
    if (isDemoBusiness()) return null;
    return isRjBirdTenantProfile(profile) ? RJ_BIRD_SIGNATURE_PATH : null;
}

// ============================================================================
// QUOTE ACCENT: the colour of the quote's coloured surfaces (G2, 2026-10-12)
// ----------------------------------------------------------------------------
// The logo bar, the title band, "Prepared For", the scope table's edge, the
// totals row, the footer, the tick above each section heading, the inclusion
// subheadings, the inclusion bullets and the item number cell's wash printed
// RJ Bird's orange (#EB7232 and the orange tints) on every business's quote,
// whatever colour the business saved in Settings
// (tenant_profiles.primary_color). A known business other than RJ Bird, with
// a six-digit colour of its own that is not that orange, now prints its own.
//
// RJ Bird's quote is pinned to the orange whatever its profile holds, so its
// printed document cannot change through this path. A business that is not
// known yet, one without a colour, a demo without one, and the brand kill
// switch (window.qm_disable_brand_from_tenant) all keep the orange too, with
// no attribute or style added to the quote, so their quote HTML is unchanged.
//
// FullQuoteEditorModal spreads rootProps onto #quote-print-content. The
// colours ride there as CSS custom properties, which the print window and the
// PDF renderer receive with the quote's outerHTML; styles/quote-accent.css
// paints the class-coloured surfaces from them, and the totals row and table
// edge read `fill` and `totalsInk` inline.
// ============================================================================

/** The orange every quote has printed in: RJ Bird's colour, the platform default. */
export const QUOTE_ACCENT_ORANGE = DEFAULT_ACCENT;

const SIX_DIGIT_HEX = /^#[0-9a-fA-F]{6}$/;
const AA_TEXT = 4.5;

export interface QuoteAccent {
    /** True only when the quote prints the business's own colour. */
    custom: boolean;
    /** The fill of the logo bar, title band, totals row, table edge and footer. */
    fill: string;
    /** Type on the totals row. */
    totalsInk: string;
    /** Type of the name wordmark on the logo bar. */
    wordmarkInk: '#111111' | '#ffffff';
    /** Spread onto #quote-print-content. Empty unless `custom`. */
    rootProps: {
        'data-quote-accent'?: 'custom';
        style?: Record<string, string>;
    };
}

interface QuoteAccentProfile extends LetterheadProfile {
    primaryColor?: string | null;
}

const ORANGE_ACCENT: QuoteAccent = Object.freeze({
    custom: false,
    fill: QUOTE_ACCENT_ORANGE,
    totalsInk: '#000000',
    wordmarkInk: '#111111',
    rootProps: Object.freeze({}),
}) as QuoteAccent;

/** Subheading type: 7:1 on white keeps 4.5:1 on the subheading's tinted band. */
const SUBHEADING_TEXT = 7;

function hexChannels(hex: string): [number, number, number] {
    return [parseInt(hex.slice(1, 3), 16), parseInt(hex.slice(3, 5), 16), parseInt(hex.slice(5, 7), 16)];
}

function toHex(channels: number[]): string {
    return `#${channels.map(v => Math.round(v).toString(16).padStart(2, '0')).join('')}`;
}

function towardBlack(hex: string, weight: number): string {
    return toHex(hexChannels(hex).map(v => v * (1 - weight)));
}

/** The colour mixed with white; weight 0.93 is the faintest tint. */
function towardWhite(hex: string, weight: number): string {
    return toHex(hexChannels(hex).map(v => v + (255 - v) * weight));
}

/** The colour itself, or the least darkened shade of it with `ratio` on white. */
function shadeReadingOnWhite(hex: string, ratio: number): string {
    for (let step = 0; step <= 10; step += 1) {
        const shade = step === 0 ? hex : towardBlack(hex, step / 10);
        if ((contrastRatio(shade, '#ffffff') ?? 0) >= ratio) return shade;
    }
    return '#000000';
}

/** The quote accent for this business's profile. */
export function quoteAccentFor(profile: QuoteAccentProfile | null | undefined): QuoteAccent {
    if (typeof window !== 'undefined' && (window as unknown as { qm_disable_brand_from_tenant?: boolean }).qm_disable_brand_from_tenant) {
        return ORANGE_ACCENT;
    }
    const known = Boolean(String(profile?.legalName ?? '').trim() || String(profile?.tradingName ?? '').trim());
    if (!known) return ORANGE_ACCENT;
    if (!isDemoBusiness() && isRjBirdTenantProfile(profile)) return ORANGE_ACCENT;
    const hex = String(profile?.primaryColor ?? '').trim();
    if (!SIX_DIGIT_HEX.test(hex) || hex.toLowerCase() === QUOTE_ACCENT_ORANGE.toLowerCase()) return ORANGE_ACCENT;
    const ink = accentContrastOn(hex)?.color ?? '#000000';
    const stops = deriveAccentStops(hex);
    const [red, green, blue] = hexChannels(hex);
    return {
        custom: true,
        fill: hex,
        totalsInk: ink,
        wordmarkInk: ink === '#ffffff' ? '#ffffff' : '#111111',
        rootProps: {
            'data-quote-accent': 'custom',
            style: {
                '--qm-quote-accent': hex,
                '--qm-quote-accent-ink': ink,
                '--qm-quote-accent-text': shadeReadingOnWhite(hex, AA_TEXT),
                '--qm-quote-accent-edge': stops['--qm-accent-edge']!,
                // The heading tick's gradient ends here, as it does on screen
                // once the app wears the colour (services/brand-accent.ts).
                '--qm-quote-accent-pressed': stops['--qm-accent-pressed']!,
                // The inclusion subheading: its band, the band's edges and the
                // ring round its square, mixed from the colour about as far as
                // RJ Bird's orange-50, -200 and -100 tints are from the orange,
                // and type dark enough to read on the band.
                '--qm-quote-accent-tint': towardWhite(hex, 0.93),
                '--qm-quote-accent-tint-edge': towardWhite(hex, 0.65),
                '--qm-quote-accent-tint-ring': towardWhite(hex, 0.84),
                '--qm-quote-accent-heading': shadeReadingOnWhite(hex, SUBHEADING_TEXT),
                // The item number cell's wash, 6% of the colour as RJ Bird's is.
                '--qm-quote-accent-wash': `rgba(${red}, ${green}, ${blue}, 0.06)`,
            },
        },
    };
}

// ============================================================================
// LETTERHEAD CONTACT DETAILS: where the quote's contact lines come from (G1)
// ----------------------------------------------------------------------------
// The contact person, position, phone, email, postal address and compliance
// line (migration 095) print on the letterhead strip, the sign-off and the
// footer. Once the business is known (its profile has a legal or trading
// name, the test the quote terms use) its profile is the only source: an
// empty field prints nothing. They used to fall back to the saved quote's
// contractor block, so a value cleared in Settings kept printing on every
// quote saved before the change, and a quote saved before migration 095
// carries RJ Bird's literals there. While the business is not known (the
// profile has not loaded, a read failed before any good read, or no name is
// set) nothing changes: a field the profile has wins, else the quote shows
// the detail saved on it (D9), and Print, PDF and Email stay held. RJ Bird
// has all six fields set, so its quote is unchanged.
// ============================================================================

export interface LetterheadContactDetails {
    dbpa: string;
    contact: string;
    position: string;
    phone: string;
    email: string;
    postal: string;
}

interface LetterheadContactProfile extends LetterheadProfile {
    complianceLine?: string | null;
    contactName?: string | null;
    contactPosition?: string | null;
    contactPhone?: string | null;
    contactEmail?: string | null;
    postalAddress?: string | null;
}

/** The quote's contact lines: only the profile once the business is known. */
export function letterheadContactDetails(
    profile: LetterheadContactProfile | null | undefined,
    savedContractor: Partial<Record<keyof LetterheadContactDetails, unknown>> | null | undefined,
): LetterheadContactDetails {
    if (profile && isKnownProfile(profile)) {
        return {
            dbpa: profile.complianceLine || '',
            contact: profile.contactName || '',
            position: profile.contactPosition || '',
            phone: profile.contactPhone || '',
            email: profile.contactEmail || '',
            postal: profile.postalAddress || '',
        };
    }
    const saved = savedContractor || {};
    const pick = (own: string | null | undefined, key: keyof LetterheadContactDetails) => (
        own || (saved[key] ? String(saved[key]) : '')
    );
    return {
        dbpa: pick(profile?.complianceLine, 'dbpa'),
        contact: pick(profile?.contactName, 'contact'),
        position: pick(profile?.contactPosition, 'position'),
        phone: pick(profile?.contactPhone, 'phone'),
        email: pick(profile?.contactEmail, 'email'),
        postal: pick(profile?.postalAddress, 'postal'),
    };
}
